import { useMemo } from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { DateTime } from 'luxon';

import { useAppSelector } from 'src/app/store/hooks';
import { DataGrid } from 'src/common/DataGrid';
import { DispatchTripRead } from 'src/common/external/bambi-api/bambiApi';
import { UnassignedTripResult } from 'src/common/external/bambi-api/routesApi';
import { safeRenderShortAddress } from 'src/common/formatAddress';
import { formatFullName } from 'src/common/util/formatFullName';

const columnHelper = createColumnHelper<
  UnassignedTripResult & { trip?: DispatchTripRead }
>();
const columns = [
  columnHelper.accessor('trip_id', {
    header: 'Trip',
    cell: ({ row }) => {
      const trip = row.original.trip;
      if (!trip) {
        return row.original.trip_id;
      }

      return (
        <div className="flex flex-row gap-2">
          <span>{formatFullName(trip.passenger)}</span>
          <span>
            {DateTime.fromISO(trip.scheduled_pickup_at).toLocaleString(
              DateTime.TIME_SIMPLE
            )}
          </span>
          <span>{safeRenderShortAddress(trip.pickup_address)}</span>
        </div>
      );
    },
    meta: {
      enableHeaderMenu: false,
    },
  }),
  columnHelper.accessor('reason', {
    header: 'Reason',
    cell: ({ row }) => row.original.reason,
    meta: {
      enableHeaderMenu: false,
    },
  }),
];

interface UnassignedTripsDataGridProps {
  tripsResult: UnassignedTripResult[];
}
export function UnassignedTripsDataGrid({
  tripsResult,
}: UnassignedTripsDataGridProps) {
  const unassignedTrips = useAppSelector(
    (state) => state.dispatcher.dispatchResponse?.unassigned_trips
  );
  const displayTripsResult = useMemo(() => {
    return tripsResult.map((result) => {
      const trip = unassignedTrips?.find((trip) => trip.id === result.trip_id);
      return {
        ...result,
        trip,
      };
    });
  }, [tripsResult, unassignedTrips]);

  return (
    <DataGrid
      columns={columns}
      data={displayTripsResult}
      tableId="unassigned-trips-result"
      containerClassName="max-h-[300px]"
      includePagination={false}
    />
  );
}
